/**
 * Contract `Theme` → SciChart theme overrides.
 *
 * SciChart paints everything the model does *not* describe — the canvas, the
 * gridlines, the tick labels, the rollover cursor — from its own theme provider.
 * Until now that was a hardcoded `SciChartJSDarkv2Theme`, so the chart canvas
 * was a grey radial gradient regardless of what the workspace's theme said, and
 * a light theme was simply unreachable.
 *
 * This module is the bridge, and it is deliberately **pure**: it maps semantic
 * tokens to SciChart's brush names and returns a plain overrides object. The
 * imperative shell owns constructing the base provider (a class instance, so its
 * `getStrokeColor`/`getFillColor` methods survive) and `Object.assign`ing these
 * over it. Being pure, the mapping is unit-testable without WASM — which is how
 * we can assert "the White theme's canvas is #ffffff" at all.
 *
 * Only chart chrome lives here. Per-series colors stay in the render-plan layer
 * (`buildPanePlan`), because those come from the model.
 */

import type { IThemePartial } from "scichart";
import { resolveColorRef, type Theme } from "@qufolio/model";

/**
 * Append an 8-bit alpha suffix to a `#RRGGBB` color.
 *
 * SciChart accepts `#RRGGBBAA`, which is how the built-in themes express faint
 * fills. Anything that is not a 6-digit hex (a gradient string, an already-alpha
 * color, a CSS keyword) is returned untouched rather than corrupted.
 */
function withAlpha(color: string, alpha: string): string {
  return /^#[0-9a-fA-F]{6}$/.test(color) ? color + alpha : color;
}

/**
 * The SciChart brushes a QuFolio theme drives.
 *
 * Every value is resolved through the token namespace, so this is the only
 * place chart chrome and app chrome could ever disagree — and they can't,
 * because both read the same `ThemeTokens`.
 */
export function sciChartThemeOverrides(theme: Theme): IThemePartial {
  const token = (ref: string, fallback: string): string =>
    resolveColorRef(ref, theme) ?? fallback;

  const background = token("background", "#0e1116");
  const surface = token("surface", "#12151c");
  const surfaceAlt = token("surface.alt", surface);
  const border = token("border", "#262b36");
  const grid = token("grid", "#20242e");
  const axis = token("axis", "#2a2f3a");
  const text = token("text", "#e6e9ef");
  const textSecondary = token("text.secondary", "#9aa4b2");
  const accent = token("accent", "#4fc3f7");
  const pos = token("positive", "#26a69a");
  const neg = token("negative", "#ef5350");
  const series = [1, 2, 3, 4, 5, 6].map((n) => token(`series${n}`, accent));

  return {
    isLightBackground: theme.mode === "light",

    // The canvas. Flat, and the same value the host element uses, so the chart
    // does not sit as a visibly different rectangle inside its section.
    sciChartBackground: background,
    loadingAnimationBackground: background,
    loadingAnimationForeground: textSecondary,
    // Transparent, so the plot area shows the canvas rather than a second one.
    gridBackgroundBrush: "transparent",

    gridBorderBrush: border,
    majorGridLineBrush: grid,
    // Minor lines are the same hue at 40% — a separate token for them would be a
    // token nobody would ever set differently.
    minorGridLineBrush: withAlpha(grid, "66"),
    // The alternating bands between major gridlines — a scan aid across a wide
    // chart, so they are meant to be *seen*, faintly. `aa` rather than the `33`
    // this started at: `surface.alt` is one step off the canvas by construction,
    // so a fifth of it over the canvas is a colour nobody can find. Two thirds
    // lands a band about one step off the background — the same step the token
    // already means — and a crossed X/Y band one step further.
    //
    // Nothing here was visible until the axes were themed at all: they held
    // SciChart's dark `#20212333` whatever this said. See charts/theming docs.
    axisBandsFill: withAlpha(surfaceAlt, "aa"),
    axisBorder: axis,
    tickTextBrush: textSecondary,
    axisTitleColor: text,
    chartTitleColor: text,

    rolloverLineBrush: withAlpha(accent, "66"),
    cursorLineBrush: accent,
    rubberBandFillBrush: withAlpha(accent, "33"),
    rubberBandStrokeBrush: accent,
    legendBackgroundBrush: surface,
    labelBackgroundBrush: surfaceAlt,
    labelBorderBrush: border,
    labelForegroundBrush: text,
    textAnnotationForeground: text,
    textAnnotationBackground: surface,
    annotationsGripsBorderBrush: accent,
    annotationsGripsBackgroundBrush: surfaceAlt,

    /**
     * **Transparent, because SciChart's default repaints the drawing you are
     * looking at.**
     *
     * Its dark themes set `annotationSelectionStroke` to `#f00e0e66` — red at 40%
     * — and for a line annotation the "selection box" collapses onto the line
     * itself, so the whole stroke is tinted. It does not read as a highlight; it
     * reads as the colour having changed, and it is worst in exactly the moment it
     * matters, because a drawing is selected for as long as its colour is being
     * chosen. A `#b5ee58` yellow-green came out `#cd953b` gold:
     *
     *     0.4 × (240,14,14) + 0.6 × (181,238,88) = (205,148,58) = #cd943a
     *
     * which is the measured colour to within rounding. Every swatch in the picker
     * produced a different wrong colour, all of them shifted toward red.
     *
     * Nothing is lost by removing it. Selection is already carried by the
     * **grips** — `annotationsGripsBorderBrush` above, the accent — which SciChart
     * draws on any selected editable annotation, and by the extra pixel of weight
     * a hover adds. Both say "selected" without lying about what colour the thing
     * is.
     */
    annotationSelectionStroke: "transparent",
    overviewFillBrush: withAlpha(accent, "33"),
    scrollbarBackgroundBrush: surface,
    scrollbarBorderBrush: border,
    scrollbarGripsBackgroundBrush: surfaceAlt,
    scrollbarViewportBackgroundBrush: background,
    scrollbarViewportBorderBrush: border,

    // Defaults for series the model leaves uncolored. `pos`/`neg` are the same
    // pair the screener paints a positive cell with — one concept, one token.
    upWickColor: pos,
    downWickColor: neg,
    upBodyBrush: pos,
    downBodyBrush: neg,
    upBandSeriesLineColor: pos,
    downBandSeriesLineColor: neg,
    upBandSeriesFillColor: withAlpha(pos, "33"),
    downBandSeriesFillColor: withAlpha(neg, "33"),
    mountainLineColor: accent,
    mountainAreaBrush: withAlpha(accent, "33"),
    lineSeriesColor: series[0]!,
    columnLineColor: series[0]!,
    columnFillBrush: withAlpha(series[0]!, "aa"),
    impulseFillBrush: series[0]!,
    strokePalette: series,
    fillPalette: series.map((c) => withAlpha(c, "77")),
  };
}
